import { AxiosError } from 'axios'
import React, {
  FunctionComponent,
  useEffect,
  useCallback,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  matchPath,
  RouteComponentProps,
  StaticContext,
  useLocation
} from 'react-router'

import { PatientAccessForm } from './PatientAccessForm'
import { usePatientAccessFormBrowserWarning } from './usePatientAccessFormBrowserWarning'
import {
  IOPAPostMessage,
  OPAErrorCode,
  OPAMessagingConstants
} from './PatientAccessForm.models'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { NewFeatureElements } from '../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinNotification } from '../../components/ClinNotification'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import config from '../../services/ConfigProvider'
import { AnalyticsEvent } from '../../services/Analytics'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  cancelGetOpaUrl,
  deleteOpa,
  getOpaUrl,
  updatePatient,
  getPhysicianPatientsForOpaForm
} from '../../services/ApiService'
import {
  ProgramCatalogDto,
  PatientSummaryAssociatedToPhysiciansSearchDtoForOpaForm,
  InitiateOPAResponseDto
} from '../../types/swaggerTypes'
import { getPathValueFromUrl } from '../../utils/getPathValue'
import { getOpaMinChromeVersion } from '../../utils/outdatedChromium'
import { useOnMount } from '../../utils/useOnMount'
import {
  getDependencyErrorCode,
  getDependencyErrorMessage
} from '../../utils/getDependencyErrorMessage'
import { QueryParam } from '../BootstrapSplash/BootstrapSplashConstants'
import { DeletePAFModal } from '../OpaCheckout/DeletePAFModal'
import {
  OPAOrderTypes,
  PatientOrderStatus
} from '../Patients/PatientDashboard/Patient.model'
import { PatientOPAUrlErrorModal } from '../Patients/PatientDashboard/PatientOPAUrlErrorModal/PatientOPAUrlErrorModal'
import { ModalMode } from '../PhysicianDetails/PhysicianDetailsContainer'
import { useProgramStatus } from '../Programs/ProgramDetail/ProgramDetail.models'

interface IProgramDetailRouteParams {
  programId: string
  physicianId: string
  endpoint?: string
}
interface IPatientAccessFormProps
  extends RouteComponentProps<
    IProgramDetailRouteParams,
    StaticContext,
    LocationState
  > {}

interface LocationState {
  endpoint?: string
  from?: string
  orderType?: string
  programId?: string
  patientNumber?: string
  newFeature?: NewFeatureElements
  patientId?: number
  patientStatus?: string
  patientCreationFlag?: boolean
  stockOrder?: boolean
  deletionSuccess?: boolean
}

export interface RouteStateType {
  endpoint: string
  from: string
  patientNumber?: string
  orderType?: string
  patientId?: number
}

export const PatientAccessFormContainer: FunctionComponent<
  IPatientAccessFormProps
> = ({ match, history, location }) => {
  const { t } = useTranslation()
  const { dispatch, defaultShippingAddress, institute, portfolioCountryCode } =
    useAppContext()
  const { programId, physicianId } = match.params
  const { program, isProgramLoading } = useProgramStatus(programId)
  const [iFrameHeight, setIFrameHeight] = useState<number>(600)
  const [opaUrl, setOpaUrl] = useState<string | undefined>()
  const [isIFrameLoaded, setIsIFrameLoaded] = useState<boolean>(false)
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false)
  const [deletePAFModalMode, setDeletePAFModalMode] = React.useState<
    ModalMode | undefined
  >()
  const [deletePAFError, setDeletePAFError] = React.useState<string>()
  const [patientId, setPatientId] = useState<number | undefined>()
  const [patientNumber, setPatientNumber] = useState<string | undefined>()
  const [isOPAUrlError, setIsOPAUrlError] = useState<boolean>(false)

  //Passed down to OPAHeaderStepper to disable other button
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isCancelling, setIsCancelling] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canNavigateAway, setCanNavigateAway] = useState<boolean>(true)

  const patientCentricFeatureFlag = true

  const routeState = useLocation<RouteStateType>()
  const endpoint = routeState?.state?.endpoint ?? null

  const matchOrderDetails =
    routeState.state &&
    matchPath(routeState.state.from, {
      path: '/order/:orderId'
    })

  const matchOpaOrderSummary =
    routeState.state &&
    matchPath(routeState.state.from, {
      path: '/opa-order-summary/:orderId'
    })

  const matchPatientDetails =
    routeState.state &&
    matchPath(routeState.state.from, {
      path: '/programs/my-physicians/:physicianId/:patientId'
    })

  const matchProgramDetails =
    routeState.state &&
    matchPath(routeState.state.from, {
      path: '/programs/access-programs/:programId'
    })

  const matchOPAInProgress = matchPath(location.pathname, {
    path: '/programs/access-programs/:programId/patient-access-form/:physicianId'
  })?.isExact

  let backButtonTitle

  if (matchOrderDetails) {
    backButtonTitle = t('patient_access_form:back_to_order_details')
  } else if (matchOpaOrderSummary) {
    backButtonTitle = t('patient_access_form:back_to_checkout_confirmation')
  } else if (matchPatientDetails) {
    backButtonTitle = t('patient_access_form:back_to_patient_details')
  } else {
    backButtonTitle = t('patient_access_form:back_to_program_details')
  }

  const handleGoBack = () => {
    if (
      matchOpaOrderSummary ||
      matchPatientDetails ||
      matchProgramDetails ||
      matchOrderDetails
    ) {
      history.goBack()
    }
    if (matchOPAInProgress) {
      history.replace(`/programs/access-programs/${programId}`)
    } else {
      history.push(`/programs/access-programs/${programId}`)
    }
  }

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const opaMessage: IOPAPostMessage = event.data
      // Only receive OPA events
      if (
        opaMessage.type === OPAMessagingConstants.OpaOnlineServices ||
        opaMessage.event === OPAMessagingConstants.OpaOnlineServices
      ) {
        // Removed console.warn for linting
        // Handle Save & Exit
        if (opaMessage.message && opaMessage.message === 'Save & Exit') {
          history.push(`/programs/access-programs/${programId}`)
        }
        // Handle OPA Timeout
        if (
          opaMessage.errorCode ===
          OPAErrorCode.XXCL_OPA_LAUNCH_TIMEOUT.toString()
        ) {
          history.push(`/programs/access-programs/${programId}`)
          return
        }
        // Handle scroll top message
        if (
          opaMessage.message === 'Pop-Up Modal Triggered' &&
          opaMessage.scroll === 'TOP'
        ) {
          window.scrollTo({
            behavior: 'smooth',
            top: 0
          })
        }
        // Handle change of height message
        if (opaMessage.height && opaMessage.height !== iFrameHeight) {
          setIFrameHeight(opaMessage.height)
        }
        // Handle successful order
        if (opaMessage.status && opaMessage.order) {
          setCanNavigateAway(true)
          history.push(`/opa-checkout/${opaMessage.order}`, {
            programId: programId,
            orderType: routeState.state?.orderType,
            patientNumber: routeState.state?.patientNumber,
            patientId: routeState.state?.patientId,
            stockOrder: history.location.state.stockOrder,
            from: location?.state?.from
          })
        }
        // Handle error
        if (opaMessage.errorCode && !opaMessage.status) {
          setCanNavigateAway(true)
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              opaMessage.errorCode.toString(),
              'Error'
            )
          )
        }
      } else if (
        opaMessage.system &&
        opaMessage.system === OPAMessagingConstants.ServiceCloudCPOPA
      ) {
        if (
          opaMessage.message &&
          opaMessage.message === OPAMessagingConstants.CheckoutCreated
        ) {
          void handleSavedCheckpoint()
        }
      }
    },
    [dispatch, history, iFrameHeight, programId]
  )

  const onLoad = () => {
    setIsIFrameLoaded(true)
  }

  const handleSavedCheckpoint = async () => {
    const newStatus = PatientOrderStatus.IncompletePAF

    let currentPatientId = patientId

    if (currentPatientId === undefined && patientNumber) {
      currentPatientId = await fetchPatientId(patientNumber, physicianId)
    }

    if (physicianId && currentPatientId && patientNumber) {
      updatePatient(physicianId, currentPatientId, {
        orderStatus: newStatus
      })
        .then(() => {
          setIsSaving(false)
          setCanNavigateAway(true)
          let redirect = '/patients'
          if (
            location?.state?.from &&
            location?.state?.from !== window.location.pathname
          ) {
            redirect = location.state.from
          }
          history.push(redirect, {
            programId: '',
            patientNumber: patientNumber,
            newFeature: NewFeatureElements.ContinueOPA
          })
        })
        .catch(() => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              t('transfer_patient_to_institute:patient_detail_error')
            )
          )
        })
    }
  }

  const handleUpdatePatientStatus = async () => {
    try {
      const { patientId } = routeState.state
      if (patientId) {
        await updatePatient(
          physicianId,
          Number.parseInt(patientId.toString(), 10),
          {
            orderStatus: PatientOrderStatus.IncompletePAF
          }
        )
      }
    } catch {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Error,
          t('transfer_patient_to_institute:patient_detail_error')
        )
      )
    }
  }

  useOnMount(() => {
    const patientId = history.location?.state?.patientId
    patientId && setPatientId(patientId)
    const patientNumber = history.location?.state?.patientNumber
    patientNumber && setPatientNumber(patientNumber)

    const redirect = location?.state?.from
      ? location.state.from
      : '/patients?pageSize=10'

    const hasFlag = patientCentricFeatureFlag && !endpoint && !matchOrderDetails

    const shouldRedirect = !(
      patientNumber && history.location?.state?.orderType
    )

    if (hasFlag && shouldRedirect) {
      history.push(redirect)
    }
  })

  useEffect(() => {
    const runGetOpa: boolean = true
    let orderType: string | undefined = ''
    let patientNumber: string | undefined = ''
    orderType = history.location.state?.orderType
    patientNumber = history.location.state?.patientNumber
    const shouldRedirect = !(patientNumber && orderType)

    const fetchAccessProgramData = async () => {
      if (hasFlag) {
        let runGetOpa = false

        if (shouldRedirect) {
          if (isAccessProgramPath) {
            let contactId

            try {
              const user = JSON.parse(
                localStorage.getItem('current_user') ?? '{}'
              )
              contactId = user?.contactCard?.contactId?.toString()
            } catch (e) {
              console.error('Invalid JSON in localStorage:', e)
            }

            if (contactId && contactId !== physicianId) {
              history.push(getRedirectPath())
              return
            }

            const {
              runGetOpa: updatedRunGetOpa,
              orderType: updatedOrderType,
              patientNumber: updatedPatientNumber
            } = await handleAccessProgramRedirect()

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            runGetOpa = updatedRunGetOpa
            orderType = updatedOrderType
            patientNumber = updatedPatientNumber
          } else {
            history.push(getRedirectPath())
          }
        }
      }

      if (
        programId &&
        physicianId &&
        defaultShippingAddress &&
        !endpoint &&
        runGetOpa
      ) {
        getOpaUrl(
          programId,
          physicianId,
          defaultShippingAddress.addressId,
          orderType,
          patientNumber,
          patientCentricFeatureFlag
        )
          .then(
            (response: {
              data: InitiateOPAResponseDto & { orderNumber?: string }
            }) => {
              if (response.data.orderNumber) {
                setCanNavigateAway(true)
                history.push(`/opa-checkout/${response.data.orderNumber}`, {
                  programId: programId,
                  orderType: orderType,
                  patientNumber: patientNumber,
                  patientId: patientId,
                  stockOrder: history.location.state?.stockOrder,
                  from: location?.state?.from
                })
              } else if (response.data.endPoint) {
                setCanNavigateAway(false)
                let opaUrl = response.data.endPoint
                const formId = getFormId()
                if (formId) {
                  opaUrl = `${opaUrl}/form/${formId}`
                  localStorage.removeItem(QueryParam.ReturnUrl)
                }
                setOpaUrl(opaUrl)
              } else {
                setIsOPAUrlError(true)
                setCanNavigateAway(true)
              }
            }
          )
          .catch((error: AxiosError) => {
            if (parseOPAUrlErrorMessage(error)) {
              setIsOPAUrlError(true)
              setCanNavigateAway(true)
              return
            }
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error fetching OPA URL. ${
                  getDependencyErrorMessage(error) ?? error
                }`
              )
            )
          })
        return () => {
          cancelGetOpaUrl()
        }
      }
      if (endpoint) {
        setIsReadOnly(true)
        setOpaUrl(endpoint)
      }
    }

    void fetchAccessProgramData()
  }, [defaultShippingAddress, dispatch, endpoint, physicianId, programId])

  const hasFlag = patientCentricFeatureFlag && !endpoint && !matchOrderDetails

  const redirectToAccessForm = (
    program: string,
    physician: string,
    patientNumber: string | undefined
  ) => {
    history.push({
      pathname: `/programs/access-programs/${program}/patient-access-form/${physician}`,
      state: {
        orderType: OPAOrderTypes.Resupply,
        patientNumber,
        from: window.location.pathname
      }
    })
  }

  const handleAccessProgramRedirect = () => {
    const pathParts = location.pathname.split('/')
    const program = pathParts[3]
    const physician = pathParts[5]
    const patientNumber = pathParts[9]

    setPatientNumber(patientNumber)

    redirectToAccessForm(program, physician, patientNumber)
    return Promise.resolve({
      runGetOpa: true,
      orderType: OPAOrderTypes.Resupply,
      patientNumber
    })
  }

  const fetchPatientId = async (patientNum: string, physId: string) => {
    try {
      const searchQuery: PatientSummaryAssociatedToPhysiciansSearchDtoForOpaForm =
        {
          query: patientNum,
          filter: {
            programs: [],
            patientOrderStatuses: [],
            physicianId: Number(physId)
          },
          pagination: {
            skip: 0,
            take: 5
          },
          sorting: {
            sortBy: 'lastUpdateDate',
            order: 'ASC'
          }
        }

      const response = await getPhysicianPatientsForOpaForm(
        Number(physId),
        searchQuery
      )

      if (response?.data?.result?.length) {
        const patient = response.data.result.find(
          (p) => p.patientNumber === patientNum
        )
        return patient?.patientId
      }

      return undefined
    } catch (error) {
      console.error('Error fetching patient:', error)
      return undefined
    }
  }

  const getRedirectPath = () => {
    return location?.state?.from || '/patients?pageSize=10'
  }

  const isAccessProgramPath =
    location.pathname.startsWith('/programs/access-programs/') &&
    location.pathname.includes('/patient-access-form/')

  useEffect(() => {
    isIFrameLoaded && window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage, isIFrameLoaded])

  useEffect(() => {
    void (isIFrameLoaded && handleUpdatePatientStatus())
  }, [isIFrameLoaded])

  const getFormId = () => {
    const matchPathWithFormId = matchPath(location.pathname, {
      path: '/programs/access-programs/:programId/patient-access-form/:physicianId/form/:formId'
    })?.isExact

    if (matchPathWithFormId) {
      //get form id
      return getPathValueFromUrl(window.location.pathname, QueryParam.Form)
    }
  }

  type Deps = [boolean, ProgramCatalogDto]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(AnalyticsPageEvent.ViewPAF, {
        'Program name': dependencies[1].programName,
        'Program ID': dependencies[1].projectId,
        institute_id: institute.data?.instituteId
      })
    },
    [isProgramLoading, program],
    (dependencies: Deps) => !dependencies[0] && dependencies[1]
  )

  const handleCancelButton = () => {
    setIsCancelling(true)
    setDeletePAFModalMode(ModalMode.active)
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
      location: 'opa',
      programName: program?.programName,
      programId: programId
    })
  }

  const handleSaveAndCloseButton = (programName?: string) => {
    setIsSaving(true)
    setTimeout(() => {
      setIsSaving(false)
    }, 5000)
    const iframe = document.getElementById('opa-Iframe') as HTMLIFrameElement
    iframe?.contentWindow &&
      iframe.contentWindow.postMessage(
        {
          system: 'CLINIGEN_DIRECT_SERVICE',
          message: 'CREATE_CHECKPOINT'
        },
        '*'
      )
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.SaveAndClose, {
      programName: programName,
      programId: programId,
      location: 'opa'
    })
  }

  // handlers of delete  modal window
  const handleDeletePAFConfirmation = async () => {
    try {
      await deleteOpa(
        programId,
        physicianId,
        defaultShippingAddress?.addressId ?? '',
        patientNumber || ''
      )

      analyticsServiceSingleton.trackEvent(AnalyticsEvent.SaveAndClose, {
        page: AnalyticsPageEvent.ViewPAF,
        programId,
        physicianId,
        patientId
      })

      // Determine if this is a resupply order
      const isResupplyOrder =
        routeState?.state?.orderType === OPAOrderTypes.Resupply

      // Select appropriate status based on order type
      const newStatus = isResupplyOrder
        ? PatientOrderStatus.ReadyForResupply
        : PatientOrderStatus.BeginOrder

      // Update patient order status
      let currentPatientId = patientId

      if (currentPatientId === undefined && patientNumber) {
        currentPatientId = await fetchPatientId(patientNumber, physicianId)
      }

      if (physicianId && currentPatientId) {
        try {
          await updatePatient(physicianId, currentPatientId, {
            orderStatus: newStatus
          })
        } catch (error) {
          console.error('Error updating patient status:', error)
          // Continue with redirection even if status update fails
        }
      }

      // Add success notification to be displayed after navigation
      sessionStorage.setItem('paf_deletion_success', 'true')
      sessionStorage.setItem('paf_patient_number', patientNumber || '')
      // Also store the status type for the notification to use
      sessionStorage.setItem(
        'paf_order_type',
        isResupplyOrder ? 'resupply' : 'initial'
      )

      history.push(`/programs/access-programs/${programId}`, {
        deletionSuccess: true,
        patientNumber: patientNumber,
        patientStatus: newStatus
      })
    } catch {
      setDeletePAFModalMode(ModalMode.hasError)
      setDeletePAFError(t('patient_access_form:error_deleting_paf'))
    }
  }

  const handleDeletePAFClose = () => {
    setIsCancelling(false)
    setDeletePAFModalMode(undefined)
  }

  // Oracle's code comes from the BFF's dependencyErrorCode in every
  // environment, or from the raw body when ShowDebugInfo is on.
  const parseOPAUrlErrorMessage = (error: AxiosError) =>
    getDependencyErrorCode(error) === OPAErrorCode.XXCL_OPA_INIT_PATIENT_LOCKED

  const isVisibleNotification = (): boolean => {
    return (
      !!patientCentricFeatureFlag &&
      !!history?.location?.state?.patientCreationFlag &&
      !history?.location?.state?.stockOrder
    )
  }

  const opaMinChromeVersion = getOpaMinChromeVersion(config.opaMinChromeVersion)
  const browserWarning = usePatientAccessFormBrowserWarning(opaMinChromeVersion)

  const handleOpaUrlErrorClose = () => {
    history.push(`/patients`)
    setCanNavigateAway(true)
  }

  return (
    <>
      {isVisibleNotification() && (
        <ClinNotification
          title={t('ma_order_detail:notification_create_new_patient_title')}
          onClose={() => {}}
        />
      )}
      <DeletePAFModal
        isOpen={deletePAFModalMode !== undefined}
        isSubmitting={deletePAFModalMode === ModalMode.submitting}
        hasError={deletePAFModalMode === ModalMode.hasError}
        errorMessage={deletePAFError}
        handleConfirmation={handleDeletePAFConfirmation}
        handleClose={handleDeletePAFClose}
      />
      <PatientAccessForm
        program={program}
        isLoading={isProgramLoading}
        opaUrl={opaUrl}
        height={iFrameHeight}
        isReadOnly={isReadOnly}
        backButtonTitle={backButtonTitle}
        userCountry={portfolioCountryCode}
        patientCentricFeatureFlag={patientCentricFeatureFlag}
        isSaving={isSaving}
        isCancelling={isCancelling}
        handleIFrameLoaded={onLoad}
        browserWarning={browserWarning}
        handleGoBack={handleGoBack}
        handleCancelButton={handleCancelButton}
        handleSaveAndCloseButton={handleSaveAndCloseButton}
      />
      <PatientOPAUrlErrorModal
        isOpen={isOPAUrlError}
        onClose={handleOpaUrlErrorClose}
      />
    </>
  )
}
